import React from 'react';
import { CheckCircle, AlertCircle, Clock, XCircle, X, Loader } from 'lucide-react';

/**
 * What happened to the payment, stated on the page rather than in a toast.
 *
 * Coming back from Stripe with no acknowledgement leaves people unsure whether
 * their money went anywhere — a toast that fades after three seconds is not
 * enough for a transaction. This stays until dismissed.
 */
const TONES = {
  paid:      { Icon: CheckCircle, title: 'Payment successful',  classes: 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200',  iconClass: 'text-green-600' },
  pending:   { Icon: Clock,       title: 'Payment processing',  classes: 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200',  iconClass: 'text-amber-600' },
  cancelled: { Icon: AlertCircle, title: 'Payment cancelled',   classes: 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200',  iconClass: 'text-amber-600' },
  failed:    { Icon: XCircle,     title: 'Payment failed',      classes: 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200',              iconClass: 'text-red-600' },
};

const money = (n) => `$${Number(n).toFixed(2).replace(/\.00$/, '')}`;

export default function PaymentResultBanner({ result, verifying, onDismiss, className = '' }) {
  if (verifying) {
    return (
      <div className={`flex items-center gap-3 p-4 rounded-xl border bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200 ${className}`}>
        <Loader size={18} className="animate-spin flex-shrink-0" />
        <p className="text-sm font-medium">Confirming your payment with Stripe…</p>
      </div>
    );
  }

  if (!result) return null;

  const tone = TONES[result.state] || TONES.failed;
  const { Icon } = tone;

  return (
    <div className={`flex items-start gap-3 p-4 rounded-xl border ${tone.classes} ${className}`}>
      <Icon size={18} className={`${tone.iconClass} flex-shrink-0 mt-0.5`} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">
          {tone.title}
          {result.state === 'paid' && result.amount != null && ` — ${money(result.amount)}`}
        </p>
        <p className="text-sm mt-0.5 opacity-90">{result.message}</p>
      </div>
      {onDismiss && (
        <button onClick={onDismiss} className="opacity-60 hover:opacity-100 transition flex-shrink-0" aria-label="Dismiss">
          <X size={16} />
        </button>
      )}
    </div>
  );
}
